import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Eye, Search } from "lucide-react";
import { api } from "@/auth/AuthContext";

/**
 * RekapPT: aggregate view of PT data.
 * Currently PT only has identity fields, so we show identity + placeholder for future data.
 * The renderer is metadata-driven — when the PT profile grows, additional columns will show up automatically.
 */
export default function RekapPT() {
  const [pts, setPts] = useState([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pt-accounts").then((r) => setPts(r.data)).catch((e) => setError(e.response?.data?.detail || "Gagal memuat data."));
  }, []);

  // Columns are derived from a superset of PT fields (identity + any extended fields present).
  const columns = useMemo(() => {
    const base = [
      { key: "kodept", label: "Kode PT" },
      { key: "namapt", label: "Nama Perguruan Tinggi" },
      { key: "username", label: "Username" },
      { key: "created_at", label: "Dibuat" },
    ];
    const extended = new Set();
    for (const p of pts) {
      for (const k of Object.keys(p)) {
        if (!["id", "kodept", "namapt", "username", "created_at", "updated_at", "created_by", "updated_by"].includes(k)) {
          extended.add(k);
        }
      }
    }
    for (const k of extended) base.push({ key: k, label: k.replace(/_/g, " ") });
    return base;
  }, [pts]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return pts;
    return pts.filter((p) => `${p.kodept} ${p.namapt} ${p.username}`.toLowerCase().includes(q));
  }, [pts, query]);

  return (
    <div className="acct-wrap">
      <div className="acct-head">
        <div>
          <div className="eyebrow">REKAPITULASI PERGURUAN TINGGI</div>
          <h2>Data seluruh perguruan tinggi</h2>
          <p className="subtitle">Ringkasan identitas & data yang dikumpulkan melalui Operation Desk PT. Kolom baru akan otomatis muncul saat PT mulai mengisi data.</p>
        </div>
      </div>

      {error && <div className="error-banner" data-testid="rekap-error">{error}</div>}

      <div className="acct-actionrow">
        <div className="search-wrap"><Search size={15} /><input data-testid="rekap-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari PT…" /></div>
        <div className="muted-text" style={{ fontSize: 11 }}><Building2 size={14} /> {filtered.length} dari {pts.length} PT terdaftar</div>
      </div>

      <div className="acct-summary">
        <div className="summary-card"><span>Total PT terdaftar</span><strong>{pts.length}</strong></div>
        <div className="summary-card"><span>Kolom data aktif</span><strong>{columns.length}</strong></div>
        <div className="summary-card"><span>Field tambahan</span><strong>{columns.length - 4}</strong><small>Selain identitas dasar</small></div>
      </div>

      {pts.length === 0 ? (
        <div className="empty-state" data-testid="rekap-empty">Belum ada perguruan tinggi terdaftar. Tambahkan lewat menu Manajemen Akun.</div>
      ) : (
        <div className="table-wrap">
          <table data-testid="rekap-table">
            <thead><tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}<th style={{ width: 90 }}>Aksi</th></tr></thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`rekap-row-${p.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={typeof p[c.key] === "string" && p[c.key]?.length > 20 ? "" : "mono"}>{p[c.key] ?? "—"}</td>
                  ))}
                  <td><button className="folder-chip" data-testid={`rekap-view-${p.id}`} onClick={() => navigate(`/kipk/pt/${p.id}`)}><Eye size={12} /> Lihat</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted-text" style={{ marginTop: 12, fontSize: 11 }}>* Data tambahan akan otomatis tampil sebagai kolom baru bila PT mengisi field baru di Operation Desk PT.</p>
    </div>
  );
}
